import { useState, type ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import {
  translateDragEnd,
  type AssemblyShape,
  type DragNodeData,
  type DropTargetData,
} from './translate';

export type AssemblyDndProps = {
  /** False ⇒ render children only — no drag context, no handles expected. */
  enabled: boolean;
  /** True while a move mutation is in flight — new drags cannot start. */
  busy?: boolean;
  /** The current tree's ids, used to translate drops into afterId moves. */
  shape: AssemblyShape;
  onMoveSection: (sectionId: string, afterSectionId: string | null) => void;
  onMoveSubsection: (subsectionId: string, afterSubsectionId: string | null) => void;
  onMoveTestCase: (
    testCaseId: string,
    toSubsectionId: string,
    afterTestCaseId: string | null,
  ) => void;
  children: ReactNode;
};

/**
 * The single drag context over the assembly tree (feature 015). Sections,
 * subsections, and test-case rows register their own sortables underneath;
 * this translates every drop into the contract's afterId vocabulary and calls
 * the matching semantic callback. A ~6px pointer activation distance keeps
 * plain clicks (row navigation, toggles) working; the keyboard sensor makes
 * every handle keyboard-operable.
 */
export function AssemblyDnd({
  enabled,
  busy = false,
  shape,
  onMoveSection,
  onMoveSubsection,
  onMoveTestCase,
  children,
}: AssemblyDndProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const [activeLabel, setActiveLabel] = useState<string | null>(null);

  // F-037 (#90): keep the root element type STABLE across the `enabled` flip.
  // Always render <DndContext> instead of swapping to a Fragment when disabled —
  // a Fragment↔DndContext swap remounts the whole children subtree (incl. the
  // search <input> in UATPage) and drops keyboard focus on the first/last search
  // character. Changing props on a stable element only updates context, so
  // children keep their identity and the input keeps focus at both query
  // boundaries. When disabled the consumer gates `sectionsSortable` on the same
  // flag, so NO sortable handles render and no drag can start; the
  // onDragStart/onDragEnd `!enabled` guards below are defence-in-depth. We keep
  // `sensors` gated on `busy` only (not `enabled`): toggling the sensors array
  // length on every keystroke trips React's "useEffect deps changed size"
  // warning inside dnd-kit.
  return (
    <DndContext
      sensors={busy ? [] : sensors}
      collisionDetection={closestCorners}
      onDragStart={({ active }) => {
        if (!enabled) return;
        setActiveLabel((active.data.current as DragNodeData | undefined)?.label ?? null);
      }}
      onDragCancel={() => setActiveLabel(null)}
      onDragEnd={({ active, over }) => {
        // Always clear the overlay label first so a drag in flight when `enabled`
        // flips can never strand the <DragOverlay> ghost; only the move dispatch
        // is gated on `enabled` (Codex MINOR).
        setActiveLabel(null);
        if (busy || !enabled || over === null) return;
        const data = active.data.current as DragNodeData | undefined;
        if (data === undefined) return;
        const move = translateDragEnd(
          shape,
          String(active.id),
          data.kind,
          String(over.id),
          over.data.current as DragNodeData | DropTargetData | undefined,
        );
        if (move === null) return;
        if (move.kind === 'section') onMoveSection(move.sectionId, move.afterSectionId);
        else if (move.kind === 'subsection')
          onMoveSubsection(move.subsectionId, move.afterSubsectionId);
        else onMoveTestCase(move.testCaseId, move.toSubsectionId, move.afterTestCaseId);
      }}
    >
      {children}
      <DragOverlay>
        {activeLabel === null ? null : (
          <div
            data-drag-ghost
            className="inline-flex max-w-[420px] items-center gap-2 rounded-md border border-border bg-surface-card px-3 py-2 text-sm font-semibold text-text-primary shadow-lg"
          >
            <span className="block min-w-0 truncate">{activeLabel}</span>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
